import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import {
  subscribleLeads, addLead as fsAddLead, updateLead as fsUpdateLead, deleteLead as fsDeleteLead, bulkDeleteLeads as fsBulkDeleteLeads,
  subscribeFollowups, addFollowup as fsAddFollowup, updateFollowup as fsUpdateFollowup, deleteFollowup as fsDeleteFollowup,
  subscribeStages, addStage as fsAddStage, updateStage as fsUpdateStage, deleteStage as fsDeleteStage,
  subscribeCustomFields, addCustomField as fsAddCustomField, deleteCustomField as fsDeleteCustomField,
  subscribeCampaigns, addCampaign as fsAddCampaign, updateCampaign as fsUpdateCampaign,
  subscribeUsers,
  subscribeIntegrations, saveIntegrations as fsSaveIntegrations,
  addTeamMember as fsAddTeamMember, deleteTeamMember as fsDeleteTeamMember,
  updateUserProfile as fsUpdateUserProfile,
  subscribeWebForms, addWebForm as fsAddWebForm, updateWebForm as fsUpdateWebForm, deleteWebForm as fsDeleteWebForm,
  addAssessment as fsAddAssessment, updateAssessment as fsUpdateAssessment, subscribeAssessmentsForLead,
  seedDefaultData,
  subscribeRRSettings, saveRRSettings as fsSaveRRSettings,
  addActivityLog as fsAddActivityLog, subscribeActivityLogs, subscribeUserActivityLogs,
  subscribeCompany, updateCompany as fsUpdateCompany, ensureCompany, subscribeCompanies,
  subscribeCustomSources, saveCustomSources as fsSaveCustomSources,
  deduplicateCollections, restoreDefaultData,
  subscribeLeaveRequests,
} from '../firebase/firestore';
import { graphAPI, mapFBLead } from '../utils/facebookSync';

const CRMContext = createContext(null);

export function CRMProvider({ children }) {
  const { userProfile, firebaseUser } = useAuth();

  // The OAuth callback (backend/src/routes/oauth.js) redirects the browser to
  // `${FRONTEND_URL}/work-report?connected=...` / `?oauthError=...` after a
  // Google/Microsoft/Zoom connect attempt. This app has no client-side router
  // (currentPage is plain state), so without this check that redirect would
  // just load the SPA fresh at the default 'dashboard' page and silently drop
  // the connection result. Detect those query params once on first mount and
  // open Work Report directly so the success/error banner is actually seen.
  const [currentPage, setCurrentPage] = useState(() => {
    if (typeof window !== 'undefined' && /[?&](connected|oauthError)=/.test(window.location.search)) {
      // Backend redirects to /<returnPage>?connected=<provider> after OAuth.
      // Map the URL path back to the CRM page id so we land exactly where the
      // user initiated the connect flow (meetings, settings, workreport, etc.).
      const path = window.location.pathname.replace(/^\//, '');
      const PAGE_MAP = {
        meetings: 'meetings', settings: 'settings', workreport: 'workreport',
        'work-report': 'workreport', dashboard: 'dashboard', leads: 'leads',
      };
      return PAGE_MAP[path] || 'workreport';
    }
    return 'dashboard';
  });
  const [openLeadId,    setOpenLeadId]   = useState(null);
  const [addLeadModalOpen, setAddLeadModalOpen] = useState(false);
  const [leads,        setLeads]       = useState([]);
  const [followups,    setFollowups]   = useState([]);
  const [stages,       setStages]      = useState([]);
  const [customFields, setCustomFields]= useState([]);
  const [campaigns,    setCampaigns]   = useState([]);
  const [users,           setUsers]          = useState([]);
  const [integrations,    setIntegrations]    = useState({});
  const [webForms,        setWebForms]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [rrSettings,      setRRSettings]      = useState({ mode: 'round_robin', rrPointer: 0, queue: [], logVisibility: [] });
  const [company,         setCompany]         = useState(null);
  const [allCompanies,    setAllCompanies]    = useState([]);
  const [customSources,   setCustomSources]   = useState([]);
  const [leaveRequests,   setLeaveRequests]   = useState([]);
  const DEFAULT_PRIORITIES = [
    { value: 'hot',  label: 'Hot',  emoji: '🔥', color: '#ef4444' },
    { value: 'warm', label: 'Warm', emoji: '☀️', color: '#f59e0b' },
    { value: 'cold', label: 'Cold', emoji: '❄️', color: '#3b82f6' },
  ];
  const [priorities, setPriorities] = useState(DEFAULT_PRIORITIES);

  const unsubRefs = useRef([]);

  // Derived companyId — available everywhere in the effect.
  // IMPORTANT: no fallback to a shared/default value here. A user whose profile
  // has no companyId is not provisioned for any tenant, and must never be made
  // to share data with some other, unrelated company. Previously this fell back
  // to the literal string 'default', which silently merged every company that
  // hit the (now-fixed) companyId-dropping bug in AuthContext.js into one
  // shared bucket — that was the cross-company data leak. Treat "no companyId"
  // as "no data", full stop.
  const companyId = userProfile?.companyId || null;

  useEffect(() => {
    if (!firebaseUser || !userProfile?.companyId) {
      unsubRefs.current.forEach(fn => fn && fn());
      unsubRefs.current = [];
      setLeads([]); setFollowups([]); setStages([]);
      setCustomFields([]); setCampaigns([]); setUsers([]);
      setIntegrations({}); setWebForms([]); setCompany(null);
      setAllCompanies([]); setCustomSources([]); setLeaveRequests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const cid = userProfile.companyId;

    // Ensure company doc exists + seed defaults for new companies
    ensureCompany(cid).catch(() => {});

    let loaded = 0;
    const markLoaded = () => { loaded++; if (loaded >= 7) setLoading(false); };
    // Safety net: never block the UI for more than 6 seconds regardless of subscription errors
    const safetyTimer = setTimeout(() => { if (loaded < 7) { console.warn('[CRM] Safety timeout — forcing load complete'); setLoading(false); } }, 6000);

    const errHandler = (label) => (err) => {
      console.error(`[CRM][${label}] subscription error:`, err.code, err.message);
      markLoaded(); // still count this slot so loading can complete
    };

    const u1  = subscribleLeads(cid, data => { setLeads(data); markLoaded(); }, errHandler('leads'));
    const u2  = subscribeFollowups(cid, data => { setFollowups(data); markLoaded(); }, errHandler('followups'));
    const u3  = subscribeStages(cid, data => { setStages(data); markLoaded(); }, errHandler('stages'));
    const u4  = subscribeCustomFields(cid, data => { setCustomFields(data); markLoaded(); }, errHandler('customFields'));
    const u5  = subscribeCampaigns(cid, data => { setCampaigns(data); markLoaded(); }, errHandler('campaigns'));
    const u6  = subscribeUsers(cid, data => { setUsers(data); markLoaded(); }, errHandler('users'));
    const u7  = subscribeIntegrations(cid, data => { setIntegrations(data); markLoaded(); }, errHandler('integrations'));
    const u8  = subscribeWebForms(cid, data => { setWebForms(data); });
    const u9  = subscribeRRSettings(cid, data => { setRRSettings(data); });
    const u10 = subscribeCompany(cid, data => { setCompany(data); });
    const u12 = subscribeCustomSources(cid, data => setCustomSources(data));
    const u13 = subscribeLeaveRequests(cid, data => setLeaveRequests(data));

    // Super admin: also subscribe to all companies
    let u11 = null;
    if (userProfile?.role === 'superadmin') {
      u11 = subscribeCompanies(data => setAllCompanies(data));
    }

    unsubRefs.current = [u1, u2, u3, u4, u5, u6, u7, u8, u9, u10, u11, u12, u13].filter(Boolean);
    return () => {
      clearTimeout(safetyTimer);
      unsubRefs.current.forEach(fn => fn && fn());
    };
  }, [firebaseUser, userProfile?.companyId]); // re-subscribe if company changes

  // ─── PRIORITIES subscription (org-level custom priority labels) ──────────────
  useEffect(() => {
    if (!companyId) return;
    let unsub;
    Promise.all([import('firebase/firestore'), import('../firebase/config')]).then(([ff, fc]) => {
      unsub = ff.onSnapshot(
        ff.doc(fc.db, 'companies', companyId, 'settings', 'general'),
        snap => {
          if (snap.exists() && Array.isArray(snap.data()?.priorities) && snap.data().priorities.length) {
            setPriorities(snap.data().priorities);
          } else {
            setPriorities([
              { value: 'hot',  label: 'Hot',  emoji: '🔥', color: '#ef4444' },
              { value: 'warm', label: 'Warm', emoji: '☀️', color: '#f59e0b' },
              { value: 'cold', label: 'Cold', emoji: '❄️', color: '#3b82f6' },
            ]);
          }
        },
        () => {} // silently ignore permission errors during setup
      );
    });
    return () => unsub && unsub();
  }, [companyId]);

  // ─── LEAD NAME HELPER ────────────────────────────────────────────────────────
  const leadDisplayName = (lead) => {
    if (!lead) return '';
    const first = lead.firstName || '';
    const last  = lead.lastName  || '';
    if (first || last) return (first + ' ' + last).trim();
    return lead.name || lead.email || lead.phone || '';
  };

  // ─── ACTIVITY LOGGER ─────────────────────────────────────────────────────────
  const logActivity = useCallback((action, leadId, leadName, details = {}) => {
    if (!firebaseUser) return;
    const actor = userProfile || {};
    if (!actor.companyId) return; // unprovisioned user — never log into a shared bucket
    const cid   = actor.companyId;
    return fsAddActivityLog(cid, {
      action,
      leadId:     leadId   || null,
      leadName:   leadName || null,
      userId:     firebaseUser.uid,
      userName:   actor.name  || actor.email || 'Unknown',
      userRole:   actor.role  || 'counselor',
      userAvatar: actor.avatar || { bg: '#7c3aed', color: '#fff' },
      details,
    });
  }, [firebaseUser, userProfile]);

  // ─── DAILY LOGIN ACTIVITY LOG ─────────────────────────────────────────────────
  // Records exactly one "login" row per browser session into the company-facing
  // Activity Log (NOT the separate, company-invisible `platformUsage` tracker in
  // utils/tracker.js — that one is for superadmin analytics only and admins have
  // no read access to it). This is what actually lets an admin see "who logged in
  // and at what time" on the Activity Log page. Guarded by loginLoggedRef so it
  // fires once per uid per session — userProfile re-syncs (e.g. another field on
  // the profile changing) must NOT spam a new login row every time.
  const loginLoggedRef = useRef(null);
  useEffect(() => {
    if (!firebaseUser || !userProfile?.companyId) return;
    if (loginLoggedRef.current === firebaseUser.uid) return; // already logged this session
    loginLoggedRef.current = firebaseUser.uid;
    logActivity('login', null, null, { loginAt: new Date().toISOString() });
  }, [firebaseUser, userProfile?.companyId, logActivity]);

  // ─── ROUND-ROBIN ASSIGNMENT ───────────────────────────────────────────────────
  const rrPointerRef = useRef(0);

  const assignRoundRobin = useCallback(async () => {
    const active = users.filter(u => u.role !== 'admin');
    if (!active.length) return userProfile || null;

    const mode = rrSettings?.mode || 'round_robin';
    if (mode === 'manual') return null;
    if (mode === 'load_balanced') {
      // `u.leads` is a denormalized counter set to 0 at creation time and never
      // updated since — using it here meant "load balanced" always picked the
      // same person (everyone tied at 0). Count live from the actual leads
      // collection instead, by assignedToId, so balancing reflects reality.
      const counts = {};
      for (const l of leads) {
        if (l.assignedToId) counts[l.assignedToId] = (counts[l.assignedToId] || 0) + 1;
      }
      const sorted = [...active].sort((a, b) => (counts[a.id] || 0) - (counts[b.id] || 0));
      return sorted[0];
    }

    let pool = active;
    if (rrSettings?.queue && rrSettings.queue.length > 0) {
      const queueOrdered = rrSettings.queue.map(id => active.find(u => u.id === id)).filter(Boolean);
      if (queueOrdered.length > 0) pool = queueOrdered;
    }

    const pointer = typeof rrSettings?.rrPointer === 'number' ? rrSettings.rrPointer : 0;
    const idx = pointer % pool.length;
    const chosen = pool[idx];
    const nextPointer = (idx + 1) % pool.length;
    fsSaveRRSettings(companyId, { rrPointer: nextPointer }).catch(() => {});
    return chosen;
  }, [users, userProfile, rrSettings, companyId, leads]);

  // ─── LEADS — optimistic updates ───────────────────────────────────────────────
  const addLead = useCallback(async (data) => {
    const assigned = await assignRoundRobin();
    const firstStageId = stages[0]?.id || 'new';
    const payload = {
      ...data,
      assignedTo:   data.assignedTo   || assigned?.name || userProfile?.name || '',
      assignedToId: data.assignedToId || assigned?.id   || userProfile?.id   || '',
      score: data.score ?? Math.floor(Math.random() * 40) + 60,
      status: data.status || firstStageId,
    };

    // Optimistic: insert with a temp id immediately
    const tempId = '__temp__' + Date.now();
    const tempLead = { ...payload, id: tempId, _optimistic: true, createdAt: { seconds: Date.now() / 1000 } };
    setLeads(prev => [tempLead, ...prev]);

    try {
      const ref = await fsAddLead(companyId, payload);
      // Replace temp with real id (Firestore listener will also confirm)
      setLeads(prev => prev.map(l => l.id === tempId ? { ...tempLead, id: ref.id, _optimistic: false } : l));
      logActivity('lead_created', ref.id, leadDisplayName(data), {
        assignedTo: payload.assignedTo,
        source: data.source || '',
        stage: payload.status,
      });
      return ref;
    } catch (err) {
      // Rollback on failure
      setLeads(prev => prev.filter(l => l.id !== tempId));
      throw err;
    }
  }, [assignRoundRobin, userProfile, stages, logActivity, companyId]);

  const updateLead = useCallback(async (id, data) => {
    const existing = leads.find(l => l.id === id);

    // Optimistic: apply change instantly
    setLeads(prev => prev.map(l => l.id === id ? { ...l, ...data } : l));

    try {
      await fsUpdateLead(id, data);
      if (existing && data.status && data.status !== existing.status) {
        logActivity('stage_change', id, leadDisplayName(existing), { oldStage: existing.status, newStage: data.status });
      } else if (existing) {
        const changed = Object.keys(data).filter(k => k !== 'updatedAt' && data[k] !== existing[k]);
        if (changed.length) logActivity('lead_updated', id, leadDisplayName(existing), { fields: changed });
      }
    } catch (err) {
      // Rollback on failure
      if (existing) setLeads(prev => prev.map(l => l.id === id ? existing : l));
      throw err;
    }
  }, [leads, logActivity]);

  const deleteLead = useCallback(async (id) => {
    const existing = leads.find(l => l.id === id);

    // Optimistic: remove instantly
    setLeads(prev => prev.filter(l => l.id !== id));

    try {
      await fsDeleteLead(id);
      logActivity('lead_deleted', id, existing ? leadDisplayName(existing) : id, {});
    } catch (err) {
      // Rollback on failure
      if (existing) setLeads(prev => [existing, ...prev]);
      throw err;
    }
  }, [leads, logActivity]);

  // Bulk delete — optimistic UI update, then server-side delete (fast even for 47K leads)
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
  const bulkDeleteLeads = useCallback(async (ids) => {
    if (!ids || ids.length === 0) return;
    const idSet = new Set(ids);
    const removed = leads.filter(l => idSet.has(l.id));

    // Optimistic: remove all at once — single re-render, UI feels instant
    setLeads(prev => prev.filter(l => !idSet.has(l.id)));

    try {
      if (ids.length > 500) {
        // Large delete — offload to backend (runs parallel batches, doesn't block browser)
        await fetch(`${BACKEND_URL}/api/leads/bulk-delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyId, ids }),
        });
      } else {
        // Small delete — use client SDK directly (fast enough)
        await fsBulkDeleteLeads(ids);
      }
      logActivity('leads_bulk_deleted', null, `${ids.length} leads`, { count: ids.length });
    } catch (err) {
      // Rollback: restore all removed leads
      setLeads(prev => [...removed, ...prev]);
      throw err;
    }
  }, [leads, logActivity, companyId, BACKEND_URL]);

  // ─── FOLLOWUPS ────────────────────────────────────────────────────────────────
  const addFollowup = useCallback(async (data) => {
    const ref = await fsAddFollowup(companyId, data);
    const lead = leads.find(l => l.id === data.leadId);
    logActivity('followup_added', data.leadId, lead ? leadDisplayName(lead) : data.leadId, {
      type: data.type || '', dueDate: data.dueDate || '', note: data.note || '',
    });
    return ref;
  }, [leads, logActivity, companyId]);

  const updateFollowup = useCallback(async (id, data) => {
    await fsUpdateFollowup(id, data);
    if (data.status === 'completed' || data.completed) {
      const fu = followups.find(f => f.id === id);
      const lead = fu ? leads.find(l => l.id === fu.leadId) : null;
      logActivity('followup_completed', fu?.leadId || null, lead ? leadDisplayName(lead) : null, { type: fu?.type || '' });
    }
  }, [followups, leads, logActivity]);

  const deleteFollowup = useCallback((id) => fsDeleteFollowup(id), []);

  // ─── STAGES ───────────────────────────────────────────────────────────────────
  const addStage    = useCallback((data) => fsAddStage(companyId, { ...data, order: stages.length }), [stages, companyId]);
  const updateStage = useCallback((id, data) => fsUpdateStage(id, data), []);
  const deleteStage = useCallback((id) => fsDeleteStage(id), []);

  // ─── CUSTOM FIELDS ────────────────────────────────────────────────────────────
  const addCustomField    = useCallback((data) => fsAddCustomField(companyId, data), [companyId]);
  const deleteCustomField = useCallback((id) => fsDeleteCustomField(id), []);

  // ─── CAMPAIGNS ────────────────────────────────────────────────────────────────
  const addCampaign    = useCallback((data) => fsAddCampaign(companyId, data), [companyId]);
  const updateCampaign = useCallback((id, data) => fsUpdateCampaign(id, data), []);

  // ─── INTEGRATIONS ─────────────────────────────────────────────────────────────
  const saveIntegrations = useCallback((data) => fsSaveIntegrations(companyId, data), [companyId]);

  // ─── FACEBOOK AUTO-SYNC (background, client-side only, no backend/cron) ──────
  // The user explicitly asked NOT to touch the backend for this: Render's free
  // tier sleeps after ~15 min of no traffic, so a server-side cron isn't viable
  // without a paid plan or an external pinger. Instead, this effect lives here
  // (not in Integrations.js) so it keeps polling no matter which CRM page the
  // counselor is currently on — it only stops when the tab/app is closed.
  //
  // It only auto-syncs a form once that form has been manually synced at least
  // once via the Integrations page — that first sync is what captures the
  // field mapping (Integrations.js → handleConfirmMapping → persistFormMapping),
  // saved at integrations.facebook.pages[pageId].forms[formId] = { mapping, source }.
  // We never invent a mapping here; we only replay one the admin already approved.
  const leadsRef = useRef(leads);
  useEffect(() => { leadsRef.current = leads; }, [leads]);
  const integrationsRef = useRef(integrations);
  useEffect(() => { integrationsRef.current = integrations; }, [integrations]);
  const addLeadRef = useRef(addLead);
  useEffect(() => { addLeadRef.current = addLead; }, [addLead]);
  const saveIntegrationsRef = useRef(saveIntegrations);
  useEffect(() => { saveIntegrationsRef.current = saveIntegrations; }, [saveIntegrations]);

  useEffect(() => {
    if (!companyId) return;

    const runFacebookAutoSync = async () => {
      const fb = integrationsRef.current?.facebook;
      if (!fb?.connected || !fb.pages) return;

      const existingFbIds = new Set(
        leadsRef.current.map(l => l.fbLeadId).filter(Boolean)
      );

      for (const [pageId, pageData] of Object.entries(fb.pages)) {
        const accessToken = pageData?.accessToken;
        const forms = pageData?.forms || {};
        const mappedFormIds = Object.keys(forms).filter(fid => forms[fid]?.mapping);
        if (!accessToken || mappedFormIds.length === 0) continue;

        for (const formId of mappedFormIds) {
          const { mapping, source, name: formName } = forms[formId];
          try {
            const data = await graphAPI(`/${formId}/leads`, {
              access_token: accessToken,
              fields: 'field_data,created_time,id,form_id,ad_id,ad_name,adset_id,adset_name,campaign_id,campaign_name,platform',
              limit: 200,
            });
            // Expired token / revoked permission — stay silent. The counselor
            // will see the "token expired" banner next time they open
            // Integrations, and reconnecting refreshes it for everyone.
            if (data.error) continue;

            const newLeads = (data.data || []).filter(l => !existingFbIds.has(l.id));
            if (newLeads.length === 0) continue;

            for (const fbLead of newLeads) {
              try {
                const lead = mapFBLead(fbLead, pageId, pageData.name, formId, formName || formId, mapping, null);
                lead.source = source || lead.source || 'facebook';
                await addLeadRef.current(lead);
                existingFbIds.add(fbLead.id); // avoid re-importing within this same pass
              } catch (_) { /* skip this lead, keep going */ }
            }

            saveIntegrationsRef.current({ facebook: { [`sync_${formId}`]: new Date().toISOString() } });
          } catch (_) { /* network hiccup — next 15-min tick will retry */ }
        }
      }
    };

    // First pass shortly after the company/integrations finish loading (not
    // instant — avoids piling onto the initial subscription burst), then every
    // 15 minutes for as long as this tab stays open.
    const kickoff  = setTimeout(runFacebookAutoSync, 20000);
    const interval = setInterval(runFacebookAutoSync, 15 * 60 * 1000);
    return () => { clearTimeout(kickoff); clearInterval(interval); };
  }, [companyId]);

  // ─── WEB FORMS ────────────────────────────────────────────────────────────────
  const addWebForm    = useCallback((data) => fsAddWebForm(companyId, data), [companyId]);
  const updateWebForm = useCallback((id, data) => fsUpdateWebForm(id, data), []);
  const deleteWebForm = useCallback((id) => fsDeleteWebForm(id), []);

  // ─── ASSESSMENTS ─────────────────────────────────────────────────────────────
  const addAssessment = useCallback(async (data) => {
    const ref = await fsAddAssessment(companyId, data);
    const lead = leads.find(l => l.id === data.leadId);
    logActivity('assessment_sent', data.leadId, lead ? leadDisplayName(lead) : data.leadId, { title: data.title || '' });
    return ref;
  }, [leads, logActivity, companyId]);

  const updateAssessment = useCallback((id, data) => fsUpdateAssessment(id, data), []);

  // ─── NOTES ────────────────────────────────────────────────────────────────────
  const logNoteAdded = useCallback((leadId, noteText) => {
    const lead = leads.find(l => l.id === leadId);
    logActivity('note_added', leadId, lead ? leadDisplayName(lead) : leadId, { preview: (noteText || '').slice(0, 120) });
  }, [leads, logActivity]);

  // ─── TEAM MEMBERS ─────────────────────────────────────────────────────────────
  const AVATAR_COLORS = ['#7c3aed','#3b82f6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899'];
  const addUser = useCallback((data) => {
    const bg = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
    return fsAddTeamMember(companyId, { ...data, avatar: { bg, color: '#fff' }, leads: 0 });
  }, [companyId]);
  const deleteUser = useCallback((id) => fsDeleteTeamMember(id), []);

  const updateCurrentUser = useCallback((data) => {
    if (!firebaseUser) return Promise.resolve();
    return fsUpdateUserProfile(firebaseUser.uid, data);
  }, [firebaseUser]);

  // ─── COMPANY ──────────────────────────────────────────────────────────────────
  const companyFeatures = company?.features || {
    webForms: true, assessments: true, campaigns: true,
    integrations: true, activityLog: true, roundRobin: true,
  };
  // Data scope settings: controls what counsellors can see
  const dataScope = company?.dataScope || {
    counsellorLeads:   'own',    // 'own' | 'all'
    counsellorReports: 'own',    // 'own' | 'all'
  };
  const updateCompany = useCallback((data) => fsUpdateCompany(companyId, data), [companyId]);

  // ─── CUSTOM SOURCES ───────────────────────────────────────────────────────────
  const saveCustomSources = useCallback((sources) => fsSaveCustomSources(companyId, sources), [companyId]);

  // ─── ROUND ROBIN SETTINGS ─────────────────────────────────────────────────────
  const saveRRSettings = useCallback((data) => fsSaveRRSettings(companyId, data), [companyId]);

  // ─── SETTINGS HELPERS (exposed so Settings.js can use companyId-scoped fns) ──
  const restoreDefaults = useCallback(() => restoreDefaultData(companyId), [companyId]);
  const deduplicateData = useCallback(() => deduplicateCollections(companyId), [companyId]);

  // ─── HELPERS ──────────────────────────────────────────────────────────────────
  const getUserById      = useCallback((id) => users.find(u => u.id === id) || null, [users]);
  const getLeadById      = useCallback((id) => leads.find(l => l.id === id) || null, [leads]);
  const getLeadFollowups = useCallback((leadId) => followups.filter(f => f.leadId === leadId), [followups]);

  const currentUser = userProfile ? {
    ...userProfile,
    name:   userProfile.name   || userProfile.email || 'User',
    avatar: userProfile.avatar || { bg: '#7c3aed', color: '#fff' },
  } : {
    id: 'guest', name: 'Guest', email: '', role: 'admin',
    avatar: { bg: '#7c3aed', color: '#fff' },
  };

  const value = {
    currentPage, setCurrentPage, openLeadId, setOpenLeadId,
    addLeadModalOpen, setAddLeadModalOpen,
    leads, followups, stages,
    pipelineStages: stages,
    customFields, campaigns, users, integrations,
    loading,
    currentUser,
    addLead, updateLead, deleteLead, bulkDeleteLeads,
    addFollowup, updateFollowup, deleteFollowup,
    addStage, updateStage, deleteStage,
    addCustomField, deleteCustomField,
    addCampaign, updateCampaign,
    addUser, deleteUser, updateCurrentUser,
    assignRoundRobin,
    rrSettings, saveRRSettings,
    getUserById, getLeadById, getLeadFollowups,
    saveIntegrations,
    webForms, addWebForm, updateWebForm, deleteWebForm,
    addAssessment, updateAssessment, subscribeAssessmentsForLead,
    logActivity, logNoteAdded,
    subscribeActivityLogs: (cb, onErr) => subscribeActivityLogs(companyId, cb, onErr),
    subscribeUserActivityLogs: (userId, cb) => subscribeUserActivityLogs(companyId, userId, cb),
    company, companyId, companyFeatures, dataScope, updateCompany, allCompanies,
    customSources, saveCustomSources,
    restoreDefaults, deduplicateData,
    leaveRequests,
    priorities,
    savePriorities: async (prioArr) => {
      if (!companyId) return;
      const { setDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      await setDoc(doc(db, 'companies', companyId, 'settings', 'general'), { priorities: prioArr }, { merge: true });
    },
    // Shortlists
    addShortlist: async (data) => {
      if (!companyId) return;
      const { addDoc, collection, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      return addDoc(collection(db, 'companies', companyId, 'shortlists'), {
        ...data, companyId, createdAt: serverTimestamp(),
      });
    },
  };

  return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
}

export const useCRM = () => useContext(CRMContext);
